import { DOCUMENT } from '@angular/common';
import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { EMPTY, fromEvent, merge, Observable, Subscription, timer } from 'rxjs';
import { filter, map, startWith, switchMap, take, throttleTime } from 'rxjs/operators';

import { DataDogService } from '@amfam/shared/analytics/lib/services/data-dog.service';
import { WindowRef } from '@amfam/shared/utility/shared-services/lib/window/window.service';

import { SalesforceChatSessionService } from './salesforce-chat-session.service';

export const CHAT_IDLE_TIMEOUT_MS = 10 * 60 * 1000;
export const ACTIVITY_THROTTLE_MS = 1000;

/**
 * MyAccount activity that prevents idle timeout.
 */
export const HOST_ACTIVITY_EVENTS: readonly string[] = [
  'pointermove',
  'pointerdown',
  'keydown',
  'scroll',
  'visibilitychange'
];

/**
 * Chat widget activity that prevents idle timeout.
 */
export const WIDGET_ACTIVITY_EVENTS: readonly string[] = [
  'onEmbeddedMessagingWindowMaximized',
  'onEmbeddedMessagingWindowMinimized',
  'onEmbeddedMessageLinkClicked'
];

const HOST_LISTENER_OPTIONS: AddEventListenerOptions = { passive: true, capture: true };

/** Reads the undocumented `sessionStatus` the widget nests inside the entry payload. */
function isSessionEnded(event: Event): boolean {
  const entryPayload = (event as CustomEvent).detail?.conversationEntry?.entryPayload;
  if (!entryPayload) {
    return false;
  }
  try {
    const parsed =
      typeof entryPayload === 'string'
        ? (JSON.parse(entryPayload) as { sessionStatus?: string })
        : (entryPayload as { sessionStatus?: string });
    return parsed?.sessionStatus === 'Ended';
  } catch {
    // Payload shape is undocumented; an unparseable one just means "not ended".
    return false;
  }
}

/**
 * An agent or bot reply must not hold open a conversation the customer walked away from.
 * The role is observed as `EndUser` but documented as `End User`, so normalise instead of
 * betting on one spelling.
 */
export function isCustomerMessage(event: Event): boolean {
  const role = (event as CustomEvent).detail?.conversationEntry?.sender?.role;
  return typeof role === 'string' && role.replace(/\s+/g, '').toLowerCase() === 'enduser';
}

/**
 * Ends the Salesforce messaging session after {@link CHAT_IDLE_TIMEOUT_MS} of customer
 * inactivity.
 */
@Injectable({ providedIn: 'root' })
export class SalesforceChatIdleService implements OnDestroy {
  private win = inject(WindowRef);
  private document = inject(DOCUMENT);
  private ngZone = inject(NgZone);
  private dataDogService = inject(DataDogService);
  private chatSession = inject(SalesforceChatSessionService);

  private subscription: Subscription | undefined;

  /**
   * Begins watching for an active conversation. Safe to call more than once; only the
   * first call binds listeners.
   */
  start(): void {
    if (this.subscription) {
      return;
    }

    const win = this.win.nativeWindow;

    // Zone.js is active, and a zone-patched `pointermove` listener would run change
    // detection on every mouse move.
    this.ngZone.runOutsideAngular(() => {
      // Whether a conversation is live. `ConversationStarted` covers a new one;
      // `ConversationOpened` covers one restored on reload or in a second tab. Neither
      // payload is inspected — `ConversationOpened` carries an empty `detail`.
      //
      // Deliberately no `distinctUntilChanged()`: once the idle timer fires, `idle$` has
      // completed while this still reports `true`, so deduping would swallow the next
      // `ConversationStarted` and never restart the countdown. The harmless double reset
      // when `Started` and `Opened` both fire is the lesser evil.
      const conversationActive$ = merge(
        merge(
          fromEvent<Event>(win, 'onEmbeddedMessagingConversationStarted'),
          fromEvent<Event>(win, 'onEmbeddedMessagingConversationOpened')
        ).pipe(map(() => true)),
        merge(
          fromEvent<Event>(win, 'onEmbeddedMessagingConversationClosed'),
          fromEvent<Event>(win, 'onEmbeddedMessagingSessionStatusUpdate').pipe(
            filter(isSessionEnded)
          )
        ).pipe(map(() => false))
      ).pipe(startWith(false));

      const activitySources: Observable<Event>[] = [
        ...HOST_ACTIVITY_EVENTS.map(name =>
          fromEvent<Event>(this.document, name, HOST_LISTENER_OPTIONS)
        ),
        ...WIDGET_ACTIVITY_EVENTS.map(name => fromEvent<Event>(win, name)),
        fromEvent<Event>(win, 'onEmbeddedMessageSent').pipe(filter(isCustomerMessage))
      ];

      // Emits once, CHAT_IDLE_TIMEOUT_MS after the last sign of the customer.
      const idle$ = merge(...activitySources).pipe(
        // The countdown starts when the conversation goes active, not at first activity.
        startWith(null),
        // Leading-edge, so the deadline resets immediately. `auditTime` is trailing-edge
        // and would land every reset a second late.
        throttleTime(ACTIVITY_THROTTLE_MS),
        switchMap(() => timer(CHAT_IDLE_TIMEOUT_MS)),
        // Completing here detaches every activity listener until the next conversation.
        take(1)
      );

      this.subscription = conversationActive$
        .pipe(switchMap(active => (active ? idle$ : EMPTY)))
        .subscribe(() => this.endSession());
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
  }

  /**
   * Ends the session through the shared reset rather than calling `clearSession` directly.
   * A bare clear leaves the widget unlaunchable in `Auth` mode, which stranded the customer
   * with a chat they could not reopen; the reset rebuilds and re-verifies it.
   */
  private endSession(): void {
    // Never rejects, so there is nothing to catch.
    this.chatSession
      .reset()
      .then(() =>
        this.dataDogService.log('Salesforce chat session ended after idle timeout', 'info')
      );
  }
}
